import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteAllNotifications, type Notification } from '@enterprise/api';
import { ensureError, isDefined, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { notificationsQueryKeys } from '@feat/notifications/common/notifications-query-keys';
import { useApplicationStore } from '@hooks/application/application-store.hook';
import { useNotifier } from '@hooks/notifier/notifier.hook';

import type { NotificationsCacheContext } from './types/notifications-cache-context.type';

export const useDeleteAllNotifications = () => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });

  const queryClient = useQueryClient();
  const notifier = useNotifier();
  const listKey = notificationsQueryKeys.list();

  return useMutation<unknown, Error, void, NotificationsCacheContext>({
    mutationFn: () => {
      return deleteAllNotifications(enterpriseApi);
    },
    onError: (e, _vars, context) => {
      if (isDefined(context)) {
        queryClient.setQueryData(listKey, context.snapshot);
      }

      notifier.error({ description: ensureError(e).message, title: 'Failed to delete all notifications' });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const snapshot = queryClient.getQueryData<OperationResultWithData<Notification[]>>(listKey);

      queryClient.setQueryData<OperationResultWithData<Notification[]>>(listKey, (cached) => {
        if (isNotDefined(cached)) {
          return cached;
        }

        if (cached.failure) {
          return cached;
        }

        return { ...cached, data: [] };
      });

      return { snapshot };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
};
